/**
 * AI Service
 *
 * Handles communication with Claude API for error explanations and chat
 */

import type {
  ChatMessage,
  ChatRequest,
  ErrorExplanation,
  ExplainErrorRequest,
} from './types';

const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';

/**
 * Anthropic API response structure
 */
interface AnthropicResponse {
  content: Array<{ text?: string; type?: string }>;
}

/**
 * Server proxy response structure
 */
interface ProxyResponse {
  response?: string;
  content?: string;
}

/**
 * Parsed explanation from AI response
 */
interface ParsedExplanation {
  plainEnglishSummary?: string;
  detailedExplanation?: string;
  possibleCauses?: string[];
  suggestedFixes?: Array<{ description: string; confidence?: string }>;
  affectedObjects?: string[];
  relatedDocumentation?: string[];
}

/**
 * System prompt for error explanations
 */
const ERROR_EXPLANATION_SYSTEM_PROMPT = `You are an EnergyPlus expert assistant. Your job is to help users understand and fix EnergyPlus simulation errors.

When explaining errors:
1. Use plain, non-technical language when possible
2. Be specific about what went wrong
3. Provide actionable steps to fix the issue
4. Reference specific object names from the error message

Respond in JSON format with this structure:
{
  "plainEnglishSummary": "One sentence explaining the error in simple terms",
  "detailedExplanation": "2-3 sentences with more technical details",
  "possibleCauses": ["cause1", "cause2"],
  "suggestedFixes": [
    {
      "description": "Step to fix the issue",
      "confidence": "high|medium|low"
    }
  ],
  "affectedObjects": ["ObjectName1", "ObjectName2"],
  "relatedDocumentation": ["EnergyPlus I/O Reference - Section Name"]
}`;

/**
 * System prompt for chat assistant
 */
const CHAT_SYSTEM_PROMPT = `You are an EnergyPlus expert assistant integrated into a building energy modeling application called Envelop. You help users with:

1. Understanding their energy model
2. Answering questions about EnergyPlus objects and fields
3. Suggesting improvements to their model
4. Explaining simulation results
5. Troubleshooting issues

Guidelines:
- Be concise but thorough
- Use plain language when possible
- When suggesting changes, be specific about which objects and fields
- If you're unsure, say so rather than guessing
- Reference EnergyPlus documentation when relevant

The user's model context will be provided when available.`;

/**
 * Call the Claude API directly (for client-side with user API key)
 */
async function callAnthropicDirect(
  apiKey: string,
  systemPrompt: string,
  userMessage: string,
  conversationHistory?: ChatMessage[]
): Promise<string> {
  const messages: Array<{ role: 'user' | 'assistant'; content: string }> = [];

  // Add conversation history if provided
  if (conversationHistory) {
    for (const msg of conversationHistory) {
      messages.push({
        role: msg.role,
        content: msg.content,
      });
    }
  }

  // Add current message
  messages.push({
    role: 'user',
    content: userMessage,
  });

  const response = await fetch(ANTHROPIC_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 2048,
      system: systemPrompt,
      messages,
    }),
  });

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    throw new Error(
      `API error: ${String(response.status)} - ${JSON.stringify(errorData)}`
    );
  }

  const data = (await response.json()) as AnthropicResponse;
  return data.content[0]?.text ?? '';
}

/**
 * Call the server-side proxy (Vercel serverless function)
 */
async function callServerProxy(
  endpoint: string,
  payload: Record<string, unknown>
): Promise<string> {
  const response = await fetch(`/api/ai/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    throw new Error(
      `Server error: ${String(response.status)} - ${JSON.stringify(errorData)}`
    );
  }

  const data = (await response.json()) as ProxyResponse;
  return data.response ?? data.content ?? '';
}

/**
 * Explain an EnergyPlus error using AI
 */
export async function explainError(
  request: ExplainErrorRequest,
  apiKey: string | null,
  useServerProxy: boolean
): Promise<ErrorExplanation> {
  const userMessage = `Explain this EnergyPlus ${request.severity} error:

Error message: ${request.errorText}
${request.errorContext.length > 0 ? `Context:\n${request.errorContext.join('\n')}` : ''}
${request.modelVersion ? `EnergyPlus version: ${request.modelVersion}` : ''}
${request.relevantObjects ? `Relevant objects: ${request.relevantObjects.join(', ')}` : ''}

Provide a detailed explanation and fix suggestions.`;

  let responseText: string;

  if (useServerProxy) {
    responseText = await callServerProxy('explain', {
      errorText: request.errorText,
      errorContext: request.errorContext,
      severity: request.severity,
      modelVersion: request.modelVersion,
      relevantObjects: request.relevantObjects,
    });
  } else if (apiKey) {
    responseText = await callAnthropicDirect(
      apiKey,
      ERROR_EXPLANATION_SYSTEM_PROMPT,
      userMessage
    );
  } else {
    throw new Error('No API key configured and server proxy not available');
  }

  // Parse the JSON response
  try {
    // Extract JSON from response (handle markdown code blocks)
    const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
    const jsonStr = jsonMatch && jsonMatch[1] ? jsonMatch[1] : responseText;
    const parsed = JSON.parse(jsonStr) as ParsedExplanation;

    return {
      originalError: request.errorText,
      severity: request.severity,
      plainEnglishSummary: parsed.plainEnglishSummary ?? 'Unable to summarize',
      detailedExplanation:
        parsed.detailedExplanation ?? 'No detailed explanation available',
      possibleCauses: parsed.possibleCauses ?? [],
      suggestedFixes: (parsed.suggestedFixes ?? []).map((fix) => ({
        description: fix.description,
        confidence: (fix.confidence ?? 'medium') as 'high' | 'medium' | 'low',
        autoFixAvailable: false,
      })),
      affectedObjects: parsed.affectedObjects ?? [],
      relatedDocumentation: parsed.relatedDocumentation ?? [],
    };
  } catch {
    // If JSON parsing fails, create a basic explanation from the text
    return {
      originalError: request.errorText,
      severity: request.severity,
      plainEnglishSummary: responseText.slice(0, 200),
      detailedExplanation: responseText,
      possibleCauses: [],
      suggestedFixes: [],
      affectedObjects: [],
      relatedDocumentation: [],
    };
  }
}

/**
 * Send a chat message to the AI assistant
 */
export async function sendChatMessage(
  request: ChatRequest,
  apiKey: string | null,
  useServerProxy: boolean
): Promise<string> {
  let contextInfo = '';
  if (request.modelContext) {
    const ctx = request.modelContext;
    contextInfo = `\n\nModel context:
- EnergyPlus version: ${ctx.version ?? 'unknown'}
- Object count: ${String(ctx.objectCount ?? 'unknown')}
- Zones: ${ctx.zones?.join(', ') ?? 'none'}
- HVAC systems: ${ctx.systems?.join(', ') ?? 'none'}`;
  }

  const userMessage = request.message + contextInfo;

  if (useServerProxy) {
    return callServerProxy('chat', {
      message: request.message,
      conversationHistory: request.conversationHistory,
      modelContext: request.modelContext,
    });
  } else if (apiKey) {
    return callAnthropicDirect(
      apiKey,
      CHAT_SYSTEM_PROMPT,
      userMessage,
      request.conversationHistory
    );
  } else {
    throw new Error('No API key configured and server proxy not available');
  }
}

/**
 * Check if AI service is available
 */
export function isAIAvailable(
  apiKey: string | null,
  useServerProxy: boolean
): boolean {
  return useServerProxy || apiKey !== null;
}
